"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { FaEye, FaEyeSlash, FaCircleNotch, FaCopy } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { IoMdClose } from "react-icons/io";
import MailChecker from "mailchecker";
import validator from "validator";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "login" | "signup";
}

// ─── Constants ────────────────────────────────────────────────────────────────

const IS_DEV = process.env.NODE_ENV === "development";

/**
 * The MAIN domain — where NEXTAUTH_URL is set and where Google's
 * authorized redirect URI points. OAuth callbacks ALWAYS land here.
 */
const MAIN_ORIGIN = IS_DEV
  ? "http://localhost:3000"
  : "https://padelmasterthegame.com";

// ─── Component ────────────────────────────────────────────────────────────────

export default function AuthPopup({
  isOpen,
  onClose,
  initialMode = "login",
}: AuthPopupProps): React.ReactNode {
  const t = useTranslations("Login");
  const th = useTranslations("Padel-Master-The-Game-Home-login-signin");
  const router = useRouter();
  const locale = useLocale();

  const [mounted, setMounted] = useState(false);
  const [mode, setMode] = useState<"login" | "signup">(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [error1, setError1] = useState("");
  const [error2, setError2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSuggestion, setPasswordSuggestion] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [emailsend, setEmailsend] = useState(false);
  const [emailsenderror, setEmailsenderror] = useState(false);
  const [enteremail, setEnteremail] = useState(false);

  // ── Mount ────────────────────────────────────────────────────────────────
  useEffect(() => setMounted(true), []);

  // ── ESC key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // ── Body scroll lock ─────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // ── Reset on mode switch ─────────────────────────────────────────────────
  useEffect(() => {
    setError(null);
    setError1("");
    setError2("");
    setEmailsend(false);
    setEmailsenderror(false);
    setEnteremail(false);
    setPasswordSuggestion("");
    setShowPassword(false);
    setIsCopied(false);
  }, [mode]);

  // ── Validation helpers ───────────────────────────────────────────────────
  const isValidEmail = async (
    emailValue: string
  ): Promise<{ valid: boolean; message?: string }> => {
    if (!emailValue?.trim())
      return { valid: false, message: t("Email-is-required") };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!validator.isEmail(emailValue))
      return { valid: false, message: t("Invalid-email-format") };
    if (!MailChecker.isValid(emailValue))
      return { valid: false, message: t("Disposable-emails") };
    return { valid: true };
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return t("8-characters");
    if (!/[A-Z]/.test(password)) return t("uppercase");
    if (!/[a-z]/.test(password)) return t("lowercase");
    if (!/[0-9]/.test(password)) return t("number");
    if (!/[!@#$%^&*]/.test(password)) return t("character");
    return null;
  };

  // ── Password suggestion ──────────────────────────────────────────────────
  const generatePasswordSuggestion = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let pw = "";
    for (let i = 0; i < 12; i++)
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    setPasswordSuggestion(pw);
  };

  const handleCopyPassword = () => {
    if (!passwordSuggestion) return;
    navigator.clipboard.writeText(passwordSuggestion).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  // ── Forgot password ──────────────────────────────────────────────────────
  const resetPassword = async () => {
    if (!email) {
      setEnteremail(true);
      return;
    }
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_URL}auth/users/reset_password/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (!res.ok) throw new Error();
      setEmailsend(true);
    } catch {
      setEmailsenderror(true);
    }
  };

  // ── Google sign-in ───────────────────────────────────────────────────────
  /**
   * KEY FIX — always route the OAuth callback through the MAIN domain.
   *
   * Why: Google's authorized redirect URI must match NEXTAUTH_URL exactly.
   * NEXTAUTH_URL = https://padelmasterthegame.com, so the callback always
   * lands there regardless of which subdomain the user is currently on.
   *
   * We embed ?locale=<locale> in the callbackUrl so the NextAuth redirect
   * callback knows which subdomain to forward the browser to afterward.
   * The shared session cookie (domain=.padelmasterthegame.com) means the
   * session is immediately readable on that subdomain.
   */
  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    setError(null);

    // callbackUrl MUST start with the main domain (same as NEXTAUTH_URL).
    // We carry the locale as a query param for the redirect callback to read.
    const callbackUrl = `${MAIN_ORIGIN}/account?locale=${locale}`;

    // Full-page redirect to Google — reliable, no popup issues.
    signIn("google", { callbackUrl });
    // No setIsGoogleLoading(false) — the page navigates away.
  };

  // ── Credentials sign-up ──────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setError1("");
    setError2("");

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get("email") as string;
    const password = formData.get("password") as string;

    const emailValidation = await isValidEmail(emailValue);
    if (!emailValidation.valid) {
      setError1(emailValidation.message ?? t("Invalid-email"));
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError2(passwordError);
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_URL}auth/users/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailValue, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message ?? th("Signup-failed"));
      }

      const result = await signIn("credentials", {
        redirect: false,
        email: emailValue,
        password,
      });

      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        onClose();
        router.push("/account");
        router.refresh();
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("An-error-occurred-during-sign-up")
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Credentials sign-in ──────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setError1("");
    setError2("");

    const formData = new FormData(e.currentTarget);
    const emailValue = formData.get("email") as string;
    const password = formData.get("password") as string;

    const emailValidation = await isValidEmail(emailValue);
    if (!emailValidation.valid) {
      setError1(emailValidation.message ?? t("Invalid-email"));
      setIsLoading(false);
      return;
    }

    try {
      const result = await signIn("credentials", {
        redirect: false,
        email: emailValue,
        password,
      });

      if (result?.error) {
        setError(t("Please-verify-your-email-or-password"));
      } else {
        onClose();
        router.push("/account");
        router.refresh();
      }
    } catch {
      setError(t("An-error-occurred-during-sign-in"));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Guard ────────────────────────────────────────────────────────────────
  if (!mounted || !isOpen) return null;

  // ── Render ───────────────────────────────────────────────────────────────
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-70 z-[9999] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Popup */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="relative rounded-2xl shadow-xl overflow-hidden border border-gray-600 bg-black bg-opacity-90 backdrop-blur-sm animate-fade-in-up">

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-2 right-2 text-white hover:text-gray-300 transition-colors z-10"
            >
              <IoMdClose className="w-6 h-6" />
            </button>

            <div className="p-6 sm:p-8">
              <form
                onSubmit={mode === "login" ? handleLogin : handleSignUp}
                className="space-y-4"
              >
                <h1 className="font-extrabold text-2xl text-b uppercase">
                  {th("title")}
                </h1>

                <div className="mb-6">
                  <h3 className="text-b text-sm font-semibold uppercase">
                    {mode === "login" ? th("Login") : th("Signup")}
                  </h3>
                  <h2 className="text-gray-100 text-sm mt-1">
                    {mode === "login" ? th("login-text") : th("Signup-text")}
                  </h2>
                </div>

                {/* Email */}
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("email")}
                  className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                />
                {error1 && <p className="text-b text-sm">{error1}</p>}

                {/* Password */}
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={t("password")}
                    className="w-full px-4 py-2 border text-white bg-slate-800 placeholder:text-gray-300 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-b"
                    onFocus={() => {
                      if (mode === "signup") generatePasswordSuggestion();
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 flex items-center justify-center w-6 h-6"
                  >
                    {showPassword ? (
                      <FaEyeSlash className="w-5 h-5" />
                    ) : (
                      <FaEye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Password suggestion (signup only) */}
                {mode === "signup" && passwordSuggestion && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-gray-300 text-sm">
                      {t("text-5")} {th("suggestion")}
                      <br />
                      <span className="font-semibold text-b">
                        {passwordSuggestion}
                        <button
                          type="button"
                          onClick={handleCopyPassword}
                          className="text-gray-300 hover:text-gray-700 ml-2"
                        >
                          <FaCopy className="w-3 h-3 inline" />
                        </button>
                      </span>
                    </p>
                    {isCopied && (
                      <span className="text-sm text-green-500">
                        {t("Copied")}
                      </span>
                    )}
                  </div>
                )}

                {error2 && <p className="text-b text-sm">{error2}</p>}
                {error && <p className="text-b text-sm">{error}</p>}

                {/* Forgot password (login only) */}
                {mode === "login" && (
                  <>
                    <p className="text-white text-sm">
                      {th("forgot")}{" "}
                      <span
                        className="font-semibold underline cursor-pointer"
                        onClick={resetPassword}
                      >
                        {t("text-7")}
                      </span>
                    </p>
                    {emailsend && (
                      <p className="text-b text-sm">{t("emailsend")}</p>
                    )}
                    {emailsenderror && (
                      <p className="text-b text-sm">{t("emailsenderror")}</p>
                    )}
                    {enteremail && (
                      <p className="text-b text-sm">{t("enteremail")}</p>
                    )}
                  </>
                )}

                {/* Submit */}
                <div className="flex flex-col gap-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-b text-a py-3 rounded-lg text-sm hover:bg-a hover:text-white font-semibold transition-colors capitalize flex gap-3 justify-center items-center disabled:opacity-70"
                  >
                    {isLoading ? (
                      <>
                        {mode === "login" ? th("Login") : th("Start-now")}…
                        <FaCircleNotch className="animate-spin w-5 h-5" />
                      </>
                    ) : (
                      <>{mode === "login" ? th("Login") : th("Start-now")}</>
                    )}
                  </button>

                  {/* OR divider */}
                  <div className="flex items-center gap-3 my-2">
                    <div className="flex-1 border-t border-gray-500" />
                    <p className="text-gray-400 text-sm font-medium">
                      {th("Or")}
                    </p>
                    <div className="flex-1 border-t border-gray-500" />
                  </div>

                  {/* Google */}
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={isGoogleLoading}
                    className="border border-white w-full rounded-lg py-3 px-12 bg-black hover:bg-gray-800 cursor-pointer flex gap-2 items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGoogleLoading ? (
                      <FaCircleNotch className="animate-spin w-5 h-5 text-white" />
                    ) : (
                      <>
                        <FcGoogle size={24} />
                        <p className="text-white capitalize font-semibold text-sm">
                          {th("google")}
                        </p>
                      </>
                    )}
                  </button>
                </div>

                {/* Switch mode */}
                <p className="text-gray-100 text-xs text-center mt-4 capitalize">
                  {mode === "login" ? (
                    <>
                      {th("new")}{" "}
                      <span
                        onClick={() => setMode("signup")}
                        className="text-b font-semibold underline cursor-pointer"
                      >
                        {th("create")}
                      </span>
                    </>
                  ) : (
                    <>
                      {th("have-account")}{" "}
                      <span
                        onClick={() => setMode("login")}
                        className="text-b font-semibold underline cursor-pointer"
                      >
                        {th("Login-here")}
                      </span>
                    </>
                  )}
                </p>

                {/* Terms (signup only) */}
                {mode === "signup" && (
                  <p className="text-gray-400 text-xs text-center mt-2">
                    {th("registering")}{" "}
                    <Link href="/privacy-policy" className="font-semibold underline">
                      {th("Privacy-Policy")}
                    </Link>{" "}
                    {th("and")}{" "}
                    <Link
                      href="/terms-and-conditions"
                      className="font-semibold underline"
                    >
                      {th("Terms-Conditions")}
                    </Link>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
    </>,
    document.body
  );
}